/**
 * CompanyFinanceiro.js - Financeiro da Empresa
 * Visualização de faturas e uso de features
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companyBillingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  Loader2,
  Calendar,
  TrendingUp,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

// Status das faturas
const INVOICE_STATUS = {
  pending: { label: 'Pendente', color: 'bg-amber-100 text-amber-700', icon: Clock },
  paid: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  overdue: { label: 'Vencida', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

// Features labels
const FEATURE_LABELS = {
  selecao_aberta: 'Seleção Aberta',
  avaliacao_desempenho: 'Avaliação de Desempenho',
  banco_questoes_disc: 'Banco de Questões DISC',
  testes_comportamentais: 'Testes Comportamentais',
  pdi_pdc: 'PDI / PDC',
  pesquisa_clima: 'Pesquisa de Clima',
  diagnostico_nr01: 'Diagnóstico NR-01',
  portal_denuncias: 'Portal de Denúncias',
  grupos_testes: 'Grupos de Testes'
};

// Formatação de moeda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

// Formatação de data
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

export default function CompanyFinanceiro() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      setLoading(true);
      const response = await companyBillingAPI.getBilling();
      setBilling(response.data);
    } catch (error) {
      console.error('Erro ao carregar faturamento:', error);
      toast.error('Erro ao carregar informações de faturamento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Contar features ativas
  const activeFeatures = Object.values(billing?.features || {}).filter(Boolean).length;
  
  // Calcular uso do período
  const totalUsage = Object.values(billing?.usage_summary || {}).reduce(
    (sum, u) => sum + (u.quantity || 0), 0
  );

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="company-financeiro">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-green-600" />
              Financeiro
            </h1>
            <p className="text-slate-500">
              Acompanhe suas faturas e uso das funcionalidades
            </p>
          </div>
          <Button variant="outline" onClick={fetchBilling}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-sm">Seu Plano</p>
                  <p className="text-xl font-bold">
                    {billing?.plano?.tipo === 'completo' ? 'Plano Completo' : 'Individual'}
                  </p>
                </div>
                <Package className="w-8 h-8 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Valor Mensal</p>
                  <p className="text-2xl font-bold text-green-600">
                    {billing?.plano?.tipo === 'completo' 
                      ? formatCurrency(billing?.plano?.valor_mensal)
                      : 'Variável'}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Features Ativas</p>
                  <p className="text-2xl font-bold text-slate-900">{activeFeatures}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Uso Este Mês</p>
                  <p className="text-2xl font-bold text-slate-900">{totalUsage}</p>
                </div>
                <Calendar className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Contratadas */}
        <Card>
          <CardHeader>
            <CardTitle>Funcionalidades Contratadas</CardTitle>
            <CardDescription>
              Status das funcionalidades disponíveis para sua empresa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                const isActive = billing?.features?.[key];
                const pricing = billing?.features_pricing?.[key];
                
                return (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border ${
                      isActive 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-slate-200 bg-slate-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{label}</span>
                      {isActive ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    {pricing && (
                      <p className="text-xs text-slate-500 mt-1">
                        {pricing.billing_type === 'monthly' 
                          ? `${formatCurrency(pricing.monthly_price)}/mês`
                          : pricing.billing_type === 'per_use'
                          ? `${formatCurrency(pricing.per_use_price)}/uso`
                          : 'Incluído'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Uso do Período */}
        {Object.keys(billing?.usage_summary || {}).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Uso do Período ({billing?.current_period})</CardTitle>
              <CardDescription>
                Detalhamento do uso das funcionalidades cobradas por utilização
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Funcionalidade</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(billing.usage_summary).map(([feature, data]) => (
                    <TableRow key={feature}>
                      <TableCell>{FEATURE_LABELS[feature] || feature}</TableCell>
                      <TableCell className="text-right font-medium">{data.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Faturas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Minhas Faturas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billing?.invoices?.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                Nenhuma fatura encontrada
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billing?.invoices?.map((invoice) => {
                    const statusConfig = INVOICE_STATUS[invoice.status] || INVOICE_STATUS.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.period}</TableCell>
                        <TableCell className="text-slate-600">{invoice.description || '-'}</TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(invoice.amount)}
                        </TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Informação de Contato */}
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600 text-center">
              Dúvidas sobre faturamento? Entre em contato: <strong>financeiro@g2egente.com.br</strong>
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
